import { inject, Injectable } from '@angular/core';
import { COLLECTION, db } from '@shared/helpers/database';
import { BehaviorSubject, first, Observable } from 'rxjs';
import {
  BookedmarkedProperty,
  FundingTimelineEventType,
  InvestementStatus,
  Property,
  PropertyParams,
  PropertyViewPageConfiguration,
  State,
} from './properties.model';
import { dummyDataPropertiesAvailable } from '@shared/dummyData/properties-available';
import { dummyDataProperty } from '@shared/dummyData/property-405';
import {
  TimelineNode,
  TimelineNodeState,
} from '@shared/components/timeline/timeline.model';
import { DatePipe } from '@angular/common';
import { dummyDataPropertiesFunded } from '@shared/dummyData/properties-funded';
import { dummyDataPropertiesExited } from '@shared/dummyData/properties-exited';
import { NgProgressService } from '@shared/services/ng-progress.service';
import { ApiService } from './api.service';
import { Permission, Query, Role } from 'appwrite';
import { HelpersService } from '@shared/services/helpers.service';
import { AuthService } from '@shared/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class PropertiesService {
  readonly property$: Observable<Property | null>;
  readonly properties$: Observable<Property[]>;
  readonly bookmarkedProperties$: Observable<BookedmarkedProperty[]>;
  readonly isLoadingProperty$: Observable<boolean>;
  private readonly _properties$: BehaviorSubject<Property[]> =
    new BehaviorSubject<Property[]>([]);
  private readonly _property$: BehaviorSubject<Property | null> =
    new BehaviorSubject<Property | null>(null);
  private readonly _isLoadingProperty$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private readonly _bookmarkedProperties$: BehaviorSubject<
    BookedmarkedProperty[]
  > = new BehaviorSubject<BookedmarkedProperty[]>([]);

  private readonly _ngProgressService = inject(NgProgressService);
  private readonly _apiService = inject(ApiService);
  private readonly _helperService = inject(HelpersService);
  private readonly _authService = inject(AuthService);

  constructor() {
    this.properties$ = this._properties$.asObservable();
    this.property$ = this._property$.asObservable();
    this.bookmarkedProperties$ = this._bookmarkedProperties$.asObservable();
    this.isLoadingProperty$ = this._isLoadingProperty$.asObservable();
  }

  // Get list of properties
  getProperties(params: PropertyParams): void {
    if (!params.status) {
      params.status = InvestementStatus.AVAILABLE;
    }

    // Remove comments to load ORIGINAL data <----------
    const queries: string[] = [];
    queries.push(Query.equal('visible', true));

    queries.push(Query.equal('investmentStatus', [params.status]));

    if (params.states?.length > 0) {
      queries.push(Query.equal('state', params.states));
    }

    if (params.categories?.length > 0) {
      queries.push(Query.equal('category', params.categories));
    }

    this._ngProgressService.startLoader();
    db[COLLECTION.PROPERTIES]
      .list(queries)
      .then((response: { documents: any }) => {
        console.log(response.documents);

        if (params.investmentStrategyTypes.length > 0) {
          response.documents = response.documents.filter((item: any) => {
            const found = params.investmentStrategyTypes.find(
              (filter) => filter === item.investmentStrategy
            );

            if (found) {
              return item;
            }
          });
        }

        const parsedResponse: Property[] = response.documents.map(
          (property: any) => this.parseProperty(property)
        );

        this._properties$.next(parsedResponse);
        this._ngProgressService.completeLoader();
      });

    // this._apiService.getProperties().then((res) => {
    //   console.log('GET properties seedio:', res);
    // });

    // Remove comments to load DUMMY data <----------
    // this._ngProgressService.startLoader();
    // setTimeout(() => {
    //   let propertiesData = dummyDataPropertiesAvailable;
    //   switch (params.status) {
    //     case InvestementStatus.AVAILABLE: {
    //       propertiesData = dummyDataPropertiesAvailable;
    //       break;
    //     }
    //     case InvestementStatus.FUNDED: {
    //       propertiesData = dummyDataPropertiesFunded;
    //       break;
    //     }
    //     case InvestementStatus.EXITED: {
    //       propertiesData = dummyDataPropertiesExited;
    //       break;
    //     }
    //     default: {
    //       propertiesData = dummyDataPropertiesAvailable;
    //     }
    //   }

    //   this._properties$.next(propertiesData);
    //   this._ngProgressService.completeLoader();
    // }, 200);
  }

  getBookmarkedProperties(): void {
    this._ngProgressService.startLoader();

    db[COLLECTION.BOOKMARKS].list().then((response: { documents: any }) => {
      const parsedResponse: BookedmarkedProperty[] = response.documents.map(
        (bookedmarkedProperty: any) => ({
          ...bookedmarkedProperty,
          property: this.parseProperty(bookedmarkedProperty.property),
        })
      );

      this._bookmarkedProperties$.next(parsedResponse);
      this._ngProgressService.completeLoader();
    });

    // setTimeout(() => {
    //   const propertiesData = dummyDataPropertiesAvailable.filter(
    //     (item) => item.isBookmarked
    //   );

    //   this._bookmarkedPropertis$.next(propertiesData);
    //   this._ngProgressService.completeLoader();
    // }, 200);
  }

  // Get documents
  getProperty(id: string): void {
    this._isLoadingProperty$.next(true);

    db[COLLECTION.PROPERTIES]
      .list([Query.equal('$id', [id])])
      .then((response: { documents: any }) => {
        const parsedResponse: Property[] = response.documents.map(
          (property: any) => {
            property.fundingTimeline = [
              {
                type: FundingTimelineEventType.FUNDING_DATE,
                expectedAt: new Date('2025-03-24'),
              },
              {
                type: FundingTimelineEventType.DOCUMENT_DISTRIBUTION,
                expectedAt: new Date('2025-04-08'),
              },
              {
                type: FundingTimelineEventType.FIRST_RENTAL_PAYMENT,
                expectedAt: new Date('2025-05-31'),
                projectedAt: new Date('2025-04-30'),
              },
            ];

            this.preparePropertyTimelineData(property);
            return this.parseProperty(property);
          }
        );

        this._property$.next(parsedResponse[0]);
        this._isLoadingProperty$.next(false);
      });

    // setTimeout(() => {
    //   // Should place this in different possition latter
    //   this.preparePropertyTimelineData(dummyDataProperty);

    //   if (id === '405') {
    //     this._property$.next(dummyDataProperty);
    //   } else {
    //     this._property$.next({
    //       ...dummyDataProperty,
    //       $id: id,
    //     });
    //   }

    //   this._isLoadingProperty$.next(false);
    // }, 200);
  }

  preparePropertyTimelineData(property: Property): void {
    const datePipe = new DatePipe('en-US');

    property.fundingTimelineComponent = [];
    property.fundingTimeline?.forEach((event) => {
      let node: TimelineNode;
      switch (event.type) {
        case FundingTimelineEventType.FUNDING_DATE: {
          node = {
            header: event.completedAt
              ? 'Property funding complete!'
              : 'Latest funding date',
            date: event.completedAt || event.expectedAt,
            description: [
              event.completedAt
                ? 'The property has been fully funded by investors.'
                : 'This is a conservative estimate for the closing date of the property funding.',
            ],
            state: event.completedAt
              ? TimelineNodeState.COMPLETED
              : TimelineNodeState.INCOMPLETED,
          };
          break;
        }
        case FundingTimelineEventType.DOCUMENT_DISTRIBUTION: {
          node = {
            header: 'Ownership documents distributed',
            date: event.completedAt || event.expectedAt,
            description: [
              'Your Property Share Certificates will be issued 2 weeks after the property is funded.',
            ],
            state: event.completedAt
              ? TimelineNodeState.COMPLETED
              : TimelineNodeState.INCOMPLETED,
          };
          break;
        }
        case FundingTimelineEventType.FIRST_RENTAL_PAYMENT: {
          const projectedAtString = datePipe.transform(
            event.projectedAt,
            'MMMM d, y'
          );

          const expectedAtString = datePipe.transform(
            event.expectedAt,
            'MMMM d, y'
          );

          node = {
            header: 'First rental payment',
            date: event.completedAt || event.projectedAt || event.expectedAt,
            highlightedInfoIcon: 'assets/icons/shield.svg',
            highlightedInfoLabel: `Guaranteed by ${expectedAtString}`,
            description: [
              `We project that the first rental payment will be paid by ${projectedAtString}, with a guaranteed payment date no later than ${expectedAtString}.`,
            ],
            state: event.completedAt
              ? TimelineNodeState.COMPLETED
              : TimelineNodeState.INCOMPLETED,
          };
          break;
        }
        case FundingTimelineEventType.RENOVATION: {
          node = {
            header: 'Property renovations complete',
            date: event.completedAt || event.expectedAt,
            highlightedInfoIcon: '',
            highlightedInfoLabel: '',
            description: [
              'The property will be renovated to boost its market value before listing it for sale.',
            ],
            state: event.completedAt
              ? TimelineNodeState.COMPLETED
              : TimelineNodeState.INCOMPLETED,
          };
          break;
        }
        case FundingTimelineEventType.REVALUATION_AND_LISTED: {
          node = {
            header: 'Revaluation & listed to sell',
            date: event.completedAt || event.expectedAt,
            highlightedInfoIcon: '',
            highlightedInfoLabel: '',
            description: [
              'The property is re-evaluated to confirm its increased value, then listed for sale on the market.',
            ],
            state: event.completedAt
              ? TimelineNodeState.COMPLETED
              : TimelineNodeState.INCOMPLETED,
          };
          break;
        }
        case FundingTimelineEventType.EXPECTED_SALE: {
          node = {
            header: 'Expected sale of property',
            date: event.completedAt || event.expectedAt,
            highlightedInfoIcon: '',
            highlightedInfoLabel: '',
            description: [
              'Anticipated date for the property to be sold, completing the investment cycle',
            ],
            state: event.completedAt
              ? TimelineNodeState.COMPLETED
              : TimelineNodeState.INCOMPLETED,
          };
          break;
        }
      }

      property.fundingTimelineComponent!.push(node);
    });

    // Set active node
    // The active node is the latest incompleted node
    const indexOfLatestIncompleted =
      property.fundingTimelineComponent.findIndex(
        (item) => item.state === TimelineNodeState.INCOMPLETED
      );

    property.fundingTimelineComponent[indexOfLatestIncompleted].state =
      TimelineNodeState.ACTIVE;
  }

  resetProperty(): void {
    this._property$.next(null);
  }

  bookmarkProperty(propertyId: string, isBookmarked: boolean): void {
    this._authService.user$.pipe(first()).subscribe((user) => {
      const role = Role.user(user!.$id);

      const item: any = {
        property: propertyId,
      };

      if (isBookmarked) {
        db[COLLECTION.BOOKMARKS]
          .create(item, [
            Permission.read(role),
            Permission.update(role),
            Permission.delete(role),
          ])
          .then((response: { documents: any }) => {
            console.log('Bookedmarked', response);

            this._bookmarkedProperties$.next([
              ...this._bookmarkedProperties$.value,
              response as any,
            ]);
          });
      } else {
        const item = this._bookmarkedProperties$.value.find(
          (item) => item.property.$id === propertyId
        );

        db[COLLECTION.BOOKMARKS]
          .delete(item?.$id)
          .then((response: { documents: any }) => {
            this._bookmarkedProperties$.next([
              ...this._bookmarkedProperties$.value.filter(
                (item) => item.property.$id !== propertyId
              ),
            ]);
          });
      }
    });
  }

  getPropertyViewPageConfiguration(
    state: State
  ): PropertyViewPageConfiguration {
    const defaultConfig: PropertyViewPageConfiguration = {
      description: true,
      howItWorks: false,
      investmentStrategy: false,
      investmentCalculator: false,
      propertyOverview: false,
      renovations: false,
      leasingStrategy: false,
      financialsGeneric: false,
      financialsRenovation: false,
      funingTimeline: false,
      location: false,
      documents: false,
      moreQuestions: false,
    };

    const configMap: Record<State, Partial<PropertyViewPageConfiguration>> = {
      [State.OPERATIONAL]: {
        howItWorks: true,
        investmentStrategy: true,
        investmentCalculator: true,
        propertyOverview: true,
        leasingStrategy: true,
        financialsGeneric: true,
        funingTimeline: true,
        location: true,
        documents: true,
        moreQuestions: true,
      },
      [State.READY]: {
        howItWorks: true,
        investmentStrategy: true,
        investmentCalculator: true,
        propertyOverview: true,
        leasingStrategy: true,
        financialsGeneric: true,
        funingTimeline: true,
        location: true,
        documents: true,
        moreQuestions: true,
      },
      [State.RENOVATION]: {
        howItWorks: true,
        investmentStrategy: true,
        renovations: true,
        propertyOverview: true,
        financialsRenovation: true,
        funingTimeline: true,
        location: true,
        documents: true,
        moreQuestions: true,
      },
      [State.CONSTRUCTION]: {
        howItWorks: true,
        investmentStrategy: true,
        renovations: true,
        propertyOverview: true,
        financialsRenovation: true,
        funingTimeline: true,
        location: true,
        documents: true,
        moreQuestions: true,
      },
      [State.SOLD]: {
        howItWorks: true,
        investmentCalculator: true,
        propertyOverview: true,
        leasingStrategy: true,
        financialsGeneric: true,
        location: true,
        documents: true,
        moreQuestions: true,
      },
    };

    return {
      ...defaultConfig,
      ...(configMap[state] ?? {}),
    };
  }

  isPropertyBookmarked(propertyId: string): boolean {
    return this._bookmarkedProperties$.value.some((item) => {
      return item.property.$id === propertyId;
    });
  }

  private parseProperty(property: any): Property {
    // Parse images
    property.images = property.images?.map((image: string) => {
      const tmpImage = {
        id: image,
        url: this._helperService.getSafeImageUrl(image, 390, 232),
      };

      return tmpImage;
    });

    console.log(property);

    // Parse propertyOverview
    property.description = (property as any).propertyOverview;

    // Parse propertyStatus
    property.state = (property as any).propertyStatus;

    // Parse location
    const location = (property as any).location[0].split(',');

    // Parse TimelineNode
    const timelineNodes: TimelineNode[] =
      property.fundingTimelineComponent?.map((timelineNode: any) => ({
        ...timelineNode,
        state: timelineNode.state,
      }));

    return {
      ...property,
      investementStatus: property.investmentStatus,
      fundingTimeline: timelineNodes,
      location: {
        lat: parseInt(location[0]),
        lng: parseInt(location[1]),
      },
    };
  }
}
